import { useRef, useEffect, useMemo, useState } from 'react'
import { Search, Package } from 'lucide-react'
import { formatCurrency } from '@/utils/format-currency'
import { toast } from 'sonner'
import api from '@/services/api'
import { getFriendlyError } from '@/utils/getFriendlyError'
import type { PropostaItem } from '@/types'
import { extractServices } from '@/utils/dealCalculations'
import { normalizeObjectEncoding } from '@/utils/text-normalization'
import { buildProposalItemFromCatalog, mapApiPlanToCatalog, filterPlansByCatalogScope, isGalaxyPlanLabel, type PlanCatalogScope } from '@/utils/planCatalog'
import type { Plan } from '@/components/Configuracao/plansTabTypes'
import { groupPlansByCategory, PLAN_CATEGORY_COLORS } from '@/components/Configuracao/plansTabUtils'
import type { PlanOption } from './types'
import { generateId } from './types'
import {
  agendarFieldInnerControlClass,
  agendarFieldShellClass,
} from '../agendarReuniaoFieldStyles'
import { cn } from '@/utils/cn'

interface PlanSelectorProps {
  plans?: PlanOption[]
  catalogScope?: PlanCatalogScope
  onAddItem?: (item: PropostaItem) => void
  onPlanAdded?: (items: PropostaItem[], planName: string) => void
}

export function mapApiPlanToOption(plan: any): PlanOption {
  const normalizedPlan = normalizeObjectEncoding(plan)
  const baseVal = Number(normalizedPlan?.baseValue || normalizedPlan?.valorMensal || normalizedPlan?.valor || 0)
  const isRecorrente = normalizedPlan?.type === 'RECORRENTE' || normalizedPlan?.type !== 'DEMANDAS_ESPECIFICAS'
  const duration = Number(normalizedPlan?.config?.duration || normalizedPlan?.duration || 12)
  const setupValue = Number(normalizedPlan?.config?.setupValue || normalizedPlan?.valorSetup || 0)
  const configPricing = normalizedPlan?.config?.pricing

  return {
    id: normalizedPlan?.id,
    nome: normalizedPlan?.name || normalizedPlan?.nome || '',
    descricao: normalizedPlan?.description || normalizedPlan?.descricao || '',
    category: normalizedPlan?.category || '',
    valorMensal: baseVal,
    valorSetup: setupValue,
    services: extractServices(normalizedPlan),
    duration,
    raw: normalizedPlan,
    pricing: configPricing || {
      total: isRecorrente ? baseVal * duration : baseVal,
      entry: 0,
      installments: isRecorrente ? duration : 1,
      installmentValue: baseVal,
      recorrente: isRecorrente,
      duracao: duration,
    },
    showBonus: normalizedPlan?.config?.showBonus === true,
    bonus: normalizedPlan?.config?.bonus || '',
  }
}

function planOptionToGroupInput(plan: PlanOption): Plan {
  return {
    id: plan.id,
    name: plan.nome,
    category: plan.category ?? String(plan.raw?.category ?? ''),
    description: plan.descricao,
    type: 'RECORRENTE',
    baseValue: String(plan.valorMensal),
    config: {},
  }
}

export function PlanSelector({ plans: plansProp, catalogScope = 'deals', onAddItem, onPlanAdded }: PlanSelectorProps) {
  const [plansFromApi, setPlansFromApi] = useState<PlanOption[]>([])
  const [loadingPlans, setLoadingPlans] = useState(!plansProp)
  const [planSearch, setPlanSearch] = useState('')
  const [planSearchOpen, setPlanSearchOpen] = useState(false)
  const planRef = useRef<HTMLDivElement>(null)
  const plans = useMemo(() => {
    const scoped = filterPlansByCatalogScope(plansProp ?? plansFromApi, catalogScope)
    if (catalogScope !== 'rocket') return scoped
    const galaxy = scoped.filter((plan) => isGalaxyPlanLabel(plan.nome))
    return galaxy.length > 0 ? galaxy : scoped
  }, [plansProp, plansFromApi, catalogScope])

  useEffect(() => {
    if (plansProp) return
    let cancelled = false

    async function loadPlans() {
      setLoadingPlans(true)
      try {
        const { data } = await api.get('/plans')
        const rawPlans = data.data ?? data ?? []
        if (!cancelled) {
          setPlansFromApi(
            rawPlans.map((rawPlan: unknown) => mapApiPlanToOption(rawPlan)),
          )
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setPlansFromApi([])
          toast.error(getFriendlyError(err, 'Erro ao carregar planos'))
        }
      } finally {
        if (!cancelled) setLoadingPlans(false)
      }
    }

    loadPlans()
    return () => { cancelled = true }
  }, [plansProp])

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (planRef.current && !planRef.current.contains(e.target as Node)) {
        setPlanSearchOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const filteredPlans = useMemo(() => {
    if (!planSearch.trim()) return plans
    const q = planSearch.toLowerCase()
    return plans.filter(
      (p) =>
        p.nome.toLowerCase().includes(q) ||
        (p.descricao && p.descricao.toLowerCase().includes(q)) ||
        (p.category && p.category.toLowerCase().includes(q)),
    )
  }, [plans, planSearch])

  const groupedPlans = useMemo(() => {
    const byId = new Map(filteredPlans.map((plan) => [plan.id, plan]))
    const groups = groupPlansByCategory(filteredPlans.map(planOptionToGroupInput))

    return Object.entries(groups)
      .map(([categoryKey, items]) => ({
        categoryKey,
        plans: items
          .map((item) => byId.get(item.id))
          .filter((plan): plan is PlanOption => Boolean(plan)),
      }))
      .filter((group) => group.plans.length > 0)
  }, [filteredPlans])

  const handleAddPlan = (plan: PlanOption) => {
    const bundleId = generateId()
    const pricingTotal = Number(plan.pricing?.total ?? plan.valorMensal * (plan.duration || 12))
    const bundleTotal =
      pricingTotal + (plan.valorSetup && plan.valorSetup > 0 ? plan.valorSetup : 0)

    const catalogMaster = mapApiPlanToCatalog(
      (plan.raw || {
        id: plan.id,
        name: plan.nome,
        description: plan.descricao,
        baseValue: plan.valorMensal,
        type: 'RECORRENTE',
        config: {
          duration: plan.duration,
          pricing: plan.pricing,
          showBonus: plan.showBonus,
          bonus: plan.bonus,
          deliverables: undefined,
          services: plan.services,
        },
      }) as unknown as Record<string, unknown>,
    )

    const recorrenteItem: PropostaItem = {
      ...buildProposalItemFromCatalog(catalogMaster),
      id: generateId(),
      quantidade: 1,
      comboGroupId: bundleId,
      comboFinalPrice: bundleTotal,
      catalogPlanId: plan.id,
    }
    const existingPricing = recorrenteItem.pricing || plan.pricing
    const nextPricing = {
      total: Number(existingPricing?.total ?? pricingTotal),
      entry: plan.valorSetup || Number(existingPricing?.entry || 0),
      installments: Number(existingPricing?.installments || plan.duration || 12),
      installmentValue: Number(existingPricing?.installmentValue || plan.valorMensal),
      duracao: Number(existingPricing?.duracao || plan.duration || 12),
      recorrente: true,
    }
    nextPricing.total = bundleTotal
    recorrenteItem.pricing = nextPricing
    recorrenteItem.total = bundleTotal
    recorrenteItem.valorUnitario = nextPricing.installmentValue || plan.valorMensal
    const addedItems: PropostaItem[] = []
    addedItems.push(recorrenteItem)

    if (onPlanAdded) {
      onPlanAdded(addedItems, plan.nome)
    } else {
      addedItems.forEach((item) => onAddItem?.(item))
    }

    setPlanSearchOpen(false)
    setPlanSearch('')
    if (!onPlanAdded) {
      toast.success(`Plano "${plan.nome}" adicionado`)
    }
  }

  if (!loadingPlans && plans.length === 0) {
    return (
      <div className="glass-card p-4">
        <p className="text-xs text-text-label">
          {catalogScope === 'rocket'
            ? 'Nenhum plano Rocket disponível no catálogo.'
            : 'Nenhum plano disponível para proposta comercial.'}
        </p>
      </div>
    )
  }

  return (
    <div className="glass-card p-4 space-y-3" ref={planRef}>
      <h3 className="text-xs font-bold text-accent uppercase tracking-wider flex items-center gap-2">
        <Package size={14} />
        Adicionar Plano à Proposta
      </h3>
      <div className="relative">
        <div className="flex gap-2">
          <div className={cn('relative flex-1', agendarFieldShellClass, 'flex min-h-[44px] items-center py-2')}>
            <Search
              size={13}
              className="pointer-events-none absolute left-3 top-1/2 z-[1] -translate-y-1/2 text-text-muted"
              aria-hidden
            />
            <input
              value={planSearch}
              onChange={(e) => {
                setPlanSearch(e.target.value)
                setPlanSearchOpen(true)
              }}
              onFocus={async () => {
                setPlanSearchOpen(true)
                if (!plansProp) {
                  try {
                    const { data } = await api.get('/plans')
                    const rawPlans = data.data ?? data ?? []
                    setPlansFromApi(
                      rawPlans.map((rawPlan: unknown) => mapApiPlanToOption(rawPlan)),
                    )
                  } catch {
                    /* mantém lista anterior */
                  }
                }
              }}
              placeholder="Buscar planos..."
              className={cn(agendarFieldInnerControlClass, 'pl-9 pr-3')}
            />
          </div>
        </div>

        {loadingPlans && (
          <p className="text-xs text-text-label">Carregando planos...</p>
        )}

        {planSearchOpen && !loadingPlans && (
          <div className="absolute z-20 mt-1 w-full bg-bg-card border border-white/[0.06] rounded-lg shadow-xl max-h-80 overflow-y-auto">
            {filteredPlans.length === 0 ? (
              <p className="px-3 py-3 text-sm text-text-label">Nenhum plano encontrado</p>
            ) : (
              groupedPlans.map(({ categoryKey, plans: categoryPlans }) => {
                const colors = PLAN_CATEGORY_COLORS[categoryKey] || PLAN_CATEGORY_COLORS.OUTROS

                return (
                  <div key={categoryKey} className="border-b border-white/[0.06] last:border-0">
                    <div
                      className="sticky top-0 z-10 flex items-center gap-2 border-b border-white/[0.06] bg-bg-card px-3 py-2 border-l-4"
                      style={{ borderLeftColor: colors.border }}
                    >
                      <span
                        className="text-[10px] font-bold uppercase tracking-wider"
                        style={{ color: colors.text }}
                      >
                        {categoryKey}
                      </span>
                      <span className="text-[10px] text-text-muted tabular-nums">
                        {categoryPlans.length} {categoryPlans.length === 1 ? 'plano' : 'planos'}
                      </span>
                    </div>

                    {categoryPlans.map((plan) => (
                      <button
                        key={plan.id}
                        type="button"
                        onClick={() => handleAddPlan(plan)}
                        className="w-full flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-white/[0.06] transition-colors text-left border-b border-white/[0.04] last:border-0"
                      >
                        <div className="min-w-0">
                          <p className="text-sm text-text-primary">{plan.nome}</p>
                          {plan.descricao && (
                            <p className="text-xs text-text-label line-clamp-2">{plan.descricao}</p>
                          )}
                        </div>
                        <span className="text-xs text-accent font-medium shrink-0">
                          {formatCurrency(plan.valorMensal)}
                        </span>
                      </button>
                    ))}
                  </div>
                )
              })
            )}
          </div>
        )}
      </div>
    </div>
  )
}
